import { useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from 'Context/AuthContext';
import { useCompany } from 'Hooks/useCompany';
import { useLogout } from 'Hooks/useLogout';
import { checkSession } from 'Helper/auth_url_helper';
import { toast } from 'react-toastify';
import { useCookies } from 'react-cookie';

const baseURL = `${process.env.REACT_APP_BASE_URL}`;

const axiosInstance = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include',
  withCredentials: true,
});

const post = async (url, data, reqHeaders) => {
  let res;
  if (reqHeaders) {
    res = await axiosInstance.post(url, data, {
      headers: reqHeaders,
    });
  } else {
    res = await axiosInstance.post(url, data);
  }
  return res.data.data ? res.data : res;
};

const get = async (url, requestParams = {}) => {
  const res = await axiosInstance.get(baseURL + url, requestParams);
  return res.data.data ? res.data : res;
};

const patch = async (url, data) => {
  const res = axiosInstance.patch(url, data);
  return res.data.data ? res.data : res;
};

const put = async (url, data) => {
  const res = await axiosInstance.put(url, data);
  return res.data.data ? res.data : res;
};

const remove = async (url, data) => {
  const res = await axiosInstance.delete(url, data);
  return res.data.data ? res.data : res;
};

const AxiosInterceptor = ({ children }) => {
  const [_cookies, _setCookie, removeCookie] = useCookies(['token']);
  const navigate = useNavigate();
  const { dispatch: authDispatch } = useAuthContext();
  const { setCompany } = useCompany();
  const { logout } = useLogout();

  const checkUserSession = () => {
    const { hostname: hostName } = window.location;
    const workspace = hostName.split('.')[0];
    checkSession(workspace)
      .then((res) => {
        const { company, user } = res.data;
        authDispatch({ type: 'LOGIN', payload: res.data.user });
        if (company && hostName !== company.companyWebAddress) {
          window.location.hostname = company.companyWebAddress;
        } else if (!company && !user && workspace != 'app') {
          window.location.hostname = `app${hostName.slice(workspace.length)}/account-signup`;
        }
        setCompany(company);
      })
      .catch((err) => {
        console.error(err);
        setCompany(null);
      });
  };

  useEffect(() => {
    let toastId;
    const resInterceptor = (response) => {
      return response;
    };

    const reqInterceptor = (request) => {
      request.withCredentials = true;
      return request;
    };

    const errInterceptor = (error) => {
      if (error?.response?.status === 401) {
        if (toastId && toast.isActive(toastId)) {
          toast.update(toastId, {
            render: error.response.data.msg,
          });
        } else {
          toastId = toast.error(error.response.data.msg, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        removeCookie('token', {
          path: '/',
          domain: process.env.REACT_APP_DOMAIN,
        })
        authDispatch({ type: 'LOGOUT' });
        checkUserSession();
        navigate('/');
      } else if (error?.response?.status === 403) {
        if (toastId && toast.isActive(toastId)) {
          toast.update(toastId, {
            render: error.response.data.msg,
          });
        } else {
          toastId = toast.error(error.response.data.msg, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        checkUserSession();
        navigate('/');
      }
      return Promise.reject(error);
    };

    // const reqInterceptorId = axiosInstance.interceptors.request.use(
    //   (request) => {
    //     return request;
    //   },
    //   errInterceptor
    // );
    // const resInterceptorId = axiosInstance.interceptors.response.use(resInterceptor, errInterceptor);

    const reqInterceptorId = axiosInstance.interceptors.request.use(reqInterceptor);
    const resInterceptorId = axiosInstance.interceptors.response.use(resInterceptor, errInterceptor);

    checkUserSession();

    return () => {
      axiosInstance.interceptors.request.eject(reqInterceptorId);
      axiosInstance.interceptors.response.eject(resInterceptorId);
    };
  }, []);
  return children;
};

export { AxiosInterceptor, get, post, patch, put, remove };
